import { useEffect, useMemo } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Pencil } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Page } from "@/components/layout/page";
import {
  EntityNotFound,
  EntityErrorState,
} from "@/components/layout/entity-state";
import { isValidUuid } from "@/lib/utils";
import { ActionBar } from "@/components/layout/action-bar";
import { Button } from "@/components/ui/button";
import { useSetBreadcrumb } from "@/components/layout/breadcrumb-context";
import { Skeleton } from "@/components/ui/skeleton";
import { CanEdit } from "@/lib/project-access";
import { features } from "@/lib/features";

import type {
  Outline,
  ScenarioBinding,
} from "@/components/experience/scenario-detail/scenario-detail-types";
import { ScenarioHeader } from "@/components/experience/scenario-detail/scenario-header";
import { SituationBlock } from "@/components/experience/scenario-detail/scenario-situation";
import { HumanTestsGrid } from "@/components/experience/scenario-detail/human-tests-grid";
import {
  PersonasSection,
  EmptyPersonasState,
} from "@/components/experience/scenario-detail/scenario-personas-section";

/**
 * ScenarioDetailPage — the unified scenario view.
 *
 * Lives at `/scenarios/:outlineId`. One page per scenario:
 *   - Title + dateline (criticality, persona count)
 *   - Collapsible situation + tension (default collapsed)
 *   - Human tests comparison grid
 *   - Stacked persona panels — each renders its own brief, walkthroughs,
 *     findings, and Run CTA via the modular <PersonaPanel> component.
 *
 * Hash anchors (`#persona-{personaId}`) are honored: the matching panel
 * expands and scrolls into view. Backwards-compat redirects from the
 * legacy `/as/:personaId` URL preserve those external deep links.
 */
export function ScenarioDetailPage() {
  const { id: projectId, outlineId } = useParams<{
    id: string;
    outlineId: string;
  }>();
  const isValidId = isValidUuid(outlineId);
  const location = useLocation();

  const { data, isLoading, error, refetch } = trpc.outline.getById.useQuery(
    { id: outlineId ?? "" },
    { enabled: isValidId, retry: false },
  );

  // Walkthrough/finding pages link here with the scenario *binding* id
  // (walkthrough.scenarioId). When the outline lookup misses, resolve the
  // binding and redirect to its outline with the persona panel anchored.
  const tryBindingFallback = isValidId && !!error;
  const { data: binding, isLoading: bindingLoading } =
    trpc.scenario.getById.useQuery(
      { id: outlineId ?? "" },
      { enabled: tryBindingFallback, retry: false },
    );

  const outline: Outline | undefined = data;
  useSetBreadcrumb(outline?.name);
  const bindings: ScenarioBinding[] = useMemo(
    () => outline?.scenarios ?? [],
    [outline?.scenarios],
  );

  // Hash anchor: which persona panel to default-expand
  const hashPersonaId = useMemo(() => {
    const hash = location.hash;
    if (!hash.startsWith("#persona-")) return null;
    return hash.slice("#persona-".length) || null;
  }, [location.hash]);

  // Scroll the hash-targeted panel into view after data loads
  useEffect(() => {
    if (!hashPersonaId || isLoading) return;
    // Defer to next tick so the panel has rendered
    const id = `persona-${hashPersonaId}`;
    requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  }, [hashPersonaId, isLoading]);

  if (binding && projectId) {
    return (
      <Navigate
        to={`/projects/${projectId}/scenarios/${binding.outlineId}#persona-${binding.personaId}`}
        replace
      />
    );
  }

  if (isLoading || (tryBindingFallback && bindingLoading)) {
    return (
      <Page title={<Skeleton className="h-8 w-64" />}>
        <div className="space-y-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Page>
    );
  }

  if (!isValidId || error?.data?.code === "NOT_FOUND" || !outline) {
    return (
      <EntityNotFound
        title="Scenario not found"
        entityName="scenario"
        backLink={{
          to: `/projects/${projectId ?? ""}/scenarios`,
          label: "Back to Scenarios",
        }}
      />
    );
  }

  if (error) {
    return (
      <EntityErrorState
        title="Failed to load scenario"
        onRetry={() => refetch()}
        backLink={{
          to: `/projects/${projectId ?? ""}/scenarios`,
          label: "Back to Scenarios",
        }}
      />
    );
  }

  const personaCount = bindings.length;

  return (
    <Page
      title={outline.name}
      back={{
        to: `/projects/${projectId}/scenarios`,
        label: "Scenarios",
      }}
      maxWidth="max-w-[960px]"
      actions={
        <CanEdit>
          <ActionBar
            primary={
              <Button asChild>
                <Link to={`/projects/${projectId}/scenarios/${outlineId}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit scenario
                </Link>
              </Button>
            }
            secondary={
              features.liveWalkthrough
                ? [
                    <Button key="live" variant="outline" asChild>
                      <Link
                        to={`/projects/${projectId}/live-walkthrough?scenarioId=${outlineId}`}
                      >
                        Live walkthrough →
                      </Link>
                    </Button>,
                  ]
                : undefined
            }
          />
        </CanEdit>
      }
    >
      <div className="space-y-10">
        <ScenarioHeader outline={outline} personaCount={personaCount} />

        <SituationBlock
          outlineId={outlineId ?? ""}
          narrative={outline.narrative}
          tension={outline.tension}
        />

        {outline.humanTests && outline.humanTests.length > 0 && (
          <HumanTestsGrid
            projectId={projectId ?? ""}
            outlineId={outline.id}
            questions={outline.humanTests}
            bindings={bindings}
          />
        )}

        {personaCount === 0 ? (
          <EmptyPersonasState
            projectId={projectId ?? ""}
            outlineId={outlineId ?? ""}
          />
        ) : (
          <PersonasSection
            projectId={projectId ?? ""}
            outlineId={outlineId ?? ""}
            bindings={bindings}
            hashPersonaId={hashPersonaId}
          />
        )}
      </div>
    </Page>
  );
}
